import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  UploadCloud,
  Smartphone,
  CheckCircle2,
  AlertTriangle,
  FileArchive,
  X,
  Tag,
  Layers,
  HardDrive,
  Clock,
  ShieldAlert,
  Rocket,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const dateText = (value) =>
  value ? new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const fileSizeText = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const inputCls =
  "border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 bg-white w-full transition-shadow";

function StatCard({ title, value, helper, icon: Icon, tone }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight">{title}</p>
          <p className="mt-2 text-lg font-semibold text-gray-900 break-words leading-tight">{value}</p>
          {helper && <p className="mt-1 text-xs text-gray-500 leading-snug">{helper}</p>}
        </div>
        <div className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon size={17} strokeWidth={2.2} />
        </div>
      </div>
    </div>
  );
}

export default function AppBuildManager() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const authConfig = {
    headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {},
  };

  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    latestVersionCode: "",
    latestVersionName: "",
    forceUpdate: true,
    releaseNotes: "",
  });
  const [apkFile, setApkFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const fetchInfo = async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/app-version/admin`, authConfig);
      setInfo(data);
      setForm({
        latestVersionCode: data.latestVersionCode ?? "",
        latestVersionName: data.latestVersionName ?? "",
        forceUpdate: !!data.forceUpdate,
        releaseNotes: data.releaseNotes ?? "",
      });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load current build info");
    }
  };

  useEffect(() => {
    fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickFile = (file) => {
    if (!file) return;
    const isApk =
      file.type === "application/vnd.android.package-archive" ||
      file.name?.toLowerCase().endsWith(".apk");
    if (!isApk) {
      setError("Only .apk files are allowed.");
      return;
    }
    setError("");
    setApkFile(file);
  };

  const submit = async () => {
    setMessage("");
    setError("");

    if (!apkFile && !info?.apkExists) {
      setError("Choose an APK file to upload.");
      return;
    }
    if (!form.latestVersionCode || Number(form.latestVersionCode) < 1) {
      setError("Version code must be a positive number.");
      return;
    }
    if (!form.latestVersionName.trim()) {
      setError("Version name is required.");
      return;
    }

    setSaving(true);
    setUploadProgress(0);
    try {
      const body = new FormData();
      body.append("latestVersionCode", form.latestVersionCode);
      body.append("latestVersionName", form.latestVersionName.trim());
      body.append("forceUpdate", form.forceUpdate);
      body.append("releaseNotes", form.releaseNotes.trim());
      if (apkFile) body.append("apk", apkFile);

      await axios.post(`${API}/app-version`, body, {
        headers: { ...authConfig.headers, "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setMessage("New build published — installed apps will see it on next launch.");
      setApkFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchInfo();
    } catch (err) {
      if (err?.response?.status === 413) {
        setError(
          "The file is too large for the server to accept right now — this is a server (nginx) upload-size limit, not an app problem. It needs to be raised on the VPS before this will go through."
        );
      } else if (typeof err?.response?.data === "string") {
        setError(`Upload failed (server returned status ${err.response.status}).`);
      } else {
        setError(err?.response?.data?.message || err.message || "Failed to publish build");
      }
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const versionCodeIsLowOrEqual =
    info && form.latestVersionCode !== "" && Number(form.latestVersionCode) <= Number(info.latestVersionCode);

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Smartphone size={21} className="text-blue-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Mobile App Build</h1>
          <p className="text-sm text-gray-500 mt-0.5">Publish a new APK — installed apps auto-detect it on launch.</p>
        </div>
      </div>

      {message && (
        <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm mb-4">
          <CheckCircle2 size={17} className="flex-shrink-0" /> {message}
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          <AlertTriangle size={17} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {info && (
        <>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Current Live Build</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard
              title="Version"
              value={info.latestVersionName || "—"}
              helper={`build code ${info.latestVersionCode}`}
              icon={Tag}
              tone="bg-indigo-50 text-indigo-600"
            />
            <StatCard
              title="Force Update"
              value={info.forceUpdate ? "Enabled" : "Off"}
              helper={info.forceUpdate ? "App blocks until updated" : "Optional prompt only"}
              icon={ShieldAlert}
              tone={info.forceUpdate ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-500"}
            />
            <StatCard
              title="APK File"
              value={info.apkExists ? fileSizeText(info.apkSizeBytes) : "None"}
              helper={info.apkExists ? "on file" : "not uploaded yet"}
              icon={HardDrive}
              tone="bg-blue-50 text-blue-600"
            />
            <StatCard
              title="Last Updated"
              value={info.apkExists ? dateText(info.apkUpdatedAt).split(",")[0] : "—"}
              helper={info.apkExists ? dateText(info.apkUpdatedAt).split(",")[1]?.trim() : ""}
              icon={Clock}
              tone="bg-emerald-50 text-emerald-600"
            />
          </div>
        </>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2.5">
          <Rocket size={17} className="text-gray-400" />
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Publish New Build</h3>
            <p className="text-xs text-gray-400 mt-0.5">This replaces the version served to every installed app.</p>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">APK File</label>
            {apkFile ? (
              <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl px-4 py-3.5 bg-blue-50/40">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FileArchive size={18} className="text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{apkFile.name}</div>
                    <div className="text-xs text-gray-500">{fileSizeText(apkFile.size)}</div>
                  </div>
                </div>
                {!saving && (
                  <button
                    type="button"
                    onClick={() => {
                      setApkFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-gray-400 hover:text-gray-700 hover:bg-white flex-shrink-0 p-1.5 rounded-lg transition-colors"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ) : (
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                className={`flex flex-col items-center gap-2.5 border-2 border-dashed rounded-xl px-4 py-10 cursor-pointer justify-center text-center transition-colors ${
                  dragActive ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${dragActive ? "bg-blue-100" : "bg-gray-100"}`}>
                  <UploadCloud size={22} className={dragActive ? "text-blue-600" : "text-gray-400"} />
                </div>
                <div>
                  <p className={`text-sm font-medium ${dragActive ? "text-blue-700" : "text-gray-700"}`}>
                    Click to choose, or drag a .apk file here
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Up to 100 MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk,application/vnd.android.package-archive"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
              </label>
            )}
          </div>

          {saving && (
            <div className="mb-5">
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{uploadProgress < 100 ? "Uploading…" : "Processing…"}</span>
                <span className="font-medium text-gray-700">{uploadProgress}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <Layers size={14} className="text-gray-400" /> Version Code
              </label>
              <input
                type="number"
                className={inputCls}
                value={form.latestVersionCode}
                onChange={(e) => setForm({ ...form, latestVersionCode: e.target.value })}
                placeholder="e.g. 4"
              />
              <p className="text-xs text-gray-400 mt-1.5">Must match the +build number in pubspec.yaml.</p>
              {versionCodeIsLowOrEqual && (
                <p className="flex items-center gap-1 text-xs text-amber-600 mt-1.5">
                  <AlertTriangle size={12} /> Current live code is {info.latestVersionCode} — this should be higher.
                </p>
              )}
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <Tag size={14} className="text-gray-400" /> Version Name
              </label>
              <input
                className={inputCls}
                value={form.latestVersionName}
                onChange={(e) => setForm({ ...form, latestVersionName: e.target.value })}
                placeholder="e.g. 1.0.3"
              />
              <p className="text-xs text-gray-400 mt-1.5">The version shown to agents in-app.</p>
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Release Notes</label>
            <textarea
              className={inputCls}
              rows={3}
              value={form.releaseNotes}
              onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })}
              placeholder="What changed in this build?"
            />
          </div>

          <div
            className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 mb-6 transition-colors ${
              form.forceUpdate ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"
            }`}
          >
            <button
              type="button"
              role="switch"
              aria-checked={form.forceUpdate}
              onClick={() => setForm({ ...form, forceUpdate: !form.forceUpdate })}
              className={`relative flex-shrink-0 w-10 h-6 rounded-full transition-colors mt-0.5 ${
                form.forceUpdate ? "bg-amber-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  form.forceUpdate ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
            <div>
              <p className={`text-sm font-medium ${form.forceUpdate ? "text-amber-800" : "text-gray-700"}`}>Force update</p>
              <p className={`text-xs mt-0.5 ${form.forceUpdate ? "text-amber-700" : "text-gray-500"}`}>
                {form.forceUpdate
                  ? "The app will block usage until this update is installed."
                  : "Agents will be nudged but can dismiss and keep using the current version."}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-2.5 rounded-lg shadow-sm hover:shadow transition-all"
            >
              {saving ? (
                <>
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  Publishing…
                </>
              ) : (
                <>
                  <Rocket size={15} /> Publish Build
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {info?.apkExists && (
        <p className="text-xs text-gray-400 mt-4 break-all">
          Download URL: <a href={info.apkUrl} className="text-blue-600 hover:underline" target="_blank" rel="noreferrer">{info.apkUrl}</a>
        </p>
      )}
    </div>
  );
}
